import { SORT_LOCALE } from '../constants/sortConstants';
import { createValueComparator } from '../utils/sortUtils';

// A patient who needs a bed this week, with the room properties they need.
// Only real, booked patients have needs: an unfilled seat or a shadow patient
// still takes a bed, but is counted by the total and never reaches here.
export interface RoomNeedsPatient {
  readonly id: string;
  readonly name: string;
  readonly needIds: readonly string[];
}

// Who a bed problem affects, as the popover lists them.
export type AffectedPatient = Pick<RoomNeedsPatient, 'id' | 'name'>;

// A patient room is private, so each one is exactly one bed.
export interface RoomNeedsRoom {
  readonly propertyIds: readonly string[];
}

// Demand against supply for one room property: how many of the week's patients
// need it, and how many rooms offer it.
export interface RoomNeedLoad {
  readonly propertyId: string;
  readonly name: string;
  readonly needed: number;
  readonly available: number;
  // The patients needing this property, named only when it is overbooked.
  readonly affectedPatients: readonly AffectedPatient[];
}

export interface RoomNeedsAllocation {
  readonly needs: readonly RoomNeedLoad[];
  // Patients competing for fewer rooms than there are of them. Empty exactly
  // when every patient with needs can be given a room that has all of them at
  // once. See `findConflictingPatients`.
  readonly conflictingPatients: readonly AffectedPatient[];
}

const compareNames = createValueComparator(SORT_LOCALE);

const toAffected = (patients: readonly RoomNeedsPatient[]): readonly AffectedPatient[] =>
  patients
    .map(({ id, name }) => ({ id, name }))
    .sort((a, b) => compareNames(a.name, b.name));

const canUseRoom = (patient: RoomNeedsPatient, room: RoomNeedsRoom): boolean =>
  patient.needIds.every((needId) => room.propertyIds.includes(needId));

// Maximum bipartite matching of patients to rooms by augmenting paths (Kuhn).
// A location has a few dozen rooms, so the O(V·E) bound is nothing.
// Returns, per room index, the index of the patient holding it.
const matchPatientsToRooms = (
  compatibleRooms: readonly (readonly number[])[],
  roomCount: number
): (number | undefined)[] => {
  const patientByRoom: (number | undefined)[] = new Array<number | undefined>(roomCount);
  const tryPlace = (patient: number, visited: boolean[]): boolean => {
    for (const room of compatibleRooms[patient]) {
      if (visited[room]) continue;
      visited[room] = true;
      const holder = patientByRoom[room];
      if (holder === undefined || tryPlace(holder, visited)) {
        patientByRoom[room] = patient;
        return true;
      }
    }
    return false;
  };
  compatibleRooms.forEach((_, patient) => {
    tryPlace(patient, new Array<boolean>(roomCount).fill(false));
  });
  return patientByRoom;
};

// Counting each property on its own is not enough to know whether the week
// works. Two patients needing "bariatric" and "own bathroom" respectively fit
// 1/1 and 1/1, yet cannot both be placed if the only room offering either is
// the same room. The honest test is a matching: every patient with needs gets
// a distinct room that has all of them.
//
// When no such matching exists, the patients left unplaced, together with every
// patient reachable from them by swapping rooms, form a set whose usable rooms
// are all taken by the set itself and still fall short (Kőnig/Hall). That is the
// group to name: nobody inside it can be placed by moving anybody else.
const findConflictingPatients = (
  patients: readonly RoomNeedsPatient[],
  rooms: readonly RoomNeedsRoom[]
): readonly AffectedPatient[] => {
  const compatibleRooms = patients.map((patient) =>
    rooms.flatMap((room, index) => (canUseRoom(patient, room) ? [index] : []))
  );
  const patientByRoom = matchPatientsToRooms(compatibleRooms, rooms.length);
  const matched = new Set(patientByRoom.filter((p): p is number => p !== undefined));

  // Breadth-first over alternating paths; the queue only grows, and an array
  // iterator picks up entries pushed while it is being walked.
  const queue = patients.flatMap((_, index) => (matched.has(index) ? [] : [index]));
  const reached = new Set<number>(queue);
  for (const patient of queue) {
    for (const room of compatibleRooms[patient]) {
      const holder = patientByRoom[room];
      if (holder !== undefined && !reached.has(holder)) {
        reached.add(holder);
        queue.push(holder);
      }
    }
  }
  return toAffected([...reached].map((index) => patients[index]));
};

const buildNeedLoads = (
  patients: readonly RoomNeedsPatient[],
  rooms: readonly RoomNeedsRoom[],
  propertyNames: ReadonlyMap<string, string>
): readonly RoomNeedLoad[] => {
  const propertyIds = new Set([
    ...rooms.flatMap((room) => room.propertyIds),
    ...patients.flatMap((patient) => patient.needIds)
  ]);
  return [...propertyIds]
    .map((propertyId) => {
      const needing = patients.filter((patient) => patient.needIds.includes(propertyId));
      const available = rooms.filter((room) => room.propertyIds.includes(propertyId)).length;
      return {
        propertyId,
        // A property deleted since it was picked has no name left to show.
        name: propertyNames.get(propertyId) ?? propertyId,
        needed: needing.length,
        available,
        affectedPatients: needing.length > available ? toAffected(needing) : []
      };
    })
    .sort((a, b) => compareNames(a.name, b.name));
};

export const allocateRoomNeeds = (
  patients: readonly RoomNeedsPatient[],
  rooms: readonly RoomNeedsRoom[],
  propertyNames: ReadonlyMap<string, string>
): RoomNeedsAllocation => {
  // A patient without needs can take any room; whether there are enough rooms
  // for everybody is the total's question, not this one.
  const withNeeds = patients.filter((patient) => patient.needIds.length > 0);
  return {
    needs: buildNeedLoads(withNeeds, rooms, propertyNames),
    conflictingPatients: findConflictingPatients(withNeeds, rooms)
  };
};
